import React, { useState, useEffect } from 'react'
import { X, Mail, Lock, User, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react'
import { useAuth } from '../stores/authStore'
import toast from 'react-hot-toast'

export const AuthModal = ({ isOpen, onClose, onSuccess }) => {
  const { login, register } = useAuth()
  const [mode, setMode] = useState('login') // 'login' | 'register' | 'forgot'
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    bio: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})

  // 重置表单
  useEffect(() => {
    if (isOpen) {
      setFormData({
        email: '',
        username: '',
        password: '',
        confirmPassword: '',
        bio: ''
      })
      setErrors({})
      setMode('login')
    }
  }, [isOpen])

  // 处理输入变化
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // 清除相关错误
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // 验证表单
  const validateForm = () => {
    const newErrors = {}

    if (!formData.email) {
      newErrors.email = '邮箱不能为空'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = '邮箱格式不正确'
    }

    if (mode === 'register') {
      if (!formData.username) {
        newErrors.username = '用户名不能为空'
      }

      if (!formData.password) {
        newErrors.password = '密码不能为空'
      } else if (formData.password.length < 8) {
        newErrors.password = '密码至少8个字符'
      } else if (!/(?=.*[A-Za-z])(?=.*\d)/.test(formData.password)) {
        newErrors.password = '密码必须包含字母和数字'
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = '两次密码输入不一致'
      }
    } else {
      if (!formData.password) {
        newErrors.password = '密码不能为空'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // 处理登录
  const handleLogin = async (e) => {
    e.preventDefault()
    if (!validateForm()) return

    setIsLoading(true)
    try {
      const result = await login(formData.email, formData.password)
      if (result.success) {
        toast.success('登录成功！')
        onSuccess?.(result.user)
        onClose()
      } else {
        toast.error(result.error || '登录失败')
      }
    } catch (error) {
      toast.error('登录时发生错误')
    } finally {
      setIsLoading(false)
    }
  }

  // 处理注册
  const handleRegister = async (e) => {
    e.preventDefault()
    if (!validateForm()) return

    setIsLoading(true)
    try {
      const result = await register(formData)
      if (result.success) {
        toast.success('注册成功！')
        onSuccess?.(result.user)
        onClose()
      } else {
        toast.error(result.error || '注册失败')
      }
    } catch (error) {
      toast.error('注册时发生错误')
    } finally {
      setIsLoading(false)
    }
  }

  // 切换模式
  const switchMode = (newMode) => {
    setMode(newMode)
    setErrors({})
    setFormData({
      email: '',
      username: '',
      password: '',
      confirmPassword: '',
      bio: ''
    })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-dark-light/95 backdrop-blur-xl rounded-3xl p-8 w-full max-w-md border border-white/10 shadow-2xl">
        {/* 头部 */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">
            {mode === 'login' ? '登录' : mode === 'register' ? '注册' : '忘记密码'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-300"
          >
            <X size={20} />
          </button>
        </div>

        {/* 表单 */}
        <form onSubmit={mode === 'login' ? handleLogin : handleRegister} className="space-y-4">
          {/* 邮箱 */}
          <div>
            <label className="block text-white text-sm font-medium mb-2">
              邮箱地址
            </label>
            <div className="relative">
              <Mail size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full pl-12 pr-4 py-3 bg-white/5 border ${
                  errors.email ? 'border-red-500' : 'border-white/20'
                } text-white placeholder-gray-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all duration-300`}
                placeholder="请输入邮箱地址"
              />
            </div>
            {errors.email && (
              <div className="flex items-center gap-1 mt-1 text-red-400 text-sm">
                <AlertCircle size={16} />
                <span>{errors.email}</span>
              </div>
            )}
          </div>

          {/* 用户名（仅注册时显示） */}
          {mode === 'register' && (
            <div>
              <label className="block text-white text-sm font-medium mb-2">
                用户名
              </label>
              <div className="relative">
                <User size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50" />
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className={`w-full pl-12 pr-4 py-3 bg-white/5 border ${
                    errors.username ? 'border-red-500' : 'border-white/20'
                  } text-white placeholder-gray-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all duration-300`}
                  placeholder="请输入用户名"
                />
              </div>
              {errors.username && (
                <div className="flex items-center gap-1 mt-1 text-red-400 text-sm">
                  <AlertCircle size={16} />
                  <span>{errors.username}</span>
                </div>
              )}
            </div>
          )}


          {/* 密码 */}
          <div>
            <label className="block text-white text-sm font-medium mb-2">
              密码
            </label>
            <div className="relative">
              <Lock size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50" />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`w-full pl-12 pr-12 py-3 bg-white/5 border ${
                  errors.password ? 'border-red-500' : 'border-white/20'
                } text-white placeholder-gray-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all duration-300`}
                placeholder="请输入密码"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && (
              <div className="flex items-center gap-1 mt-1 text-red-400 text-sm">
                <AlertCircle size={16} />
                <span>{errors.password}</span>
              </div>
            )}
          </div>

          {/* 确认密码（仅注册时显示） */}
          {mode === 'register' && (
            <div>
              <label className="block text-white text-sm font-medium mb-2">
                确认密码
              </label>
              <div className="relative">
                <Lock size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={`w-full pl-12 pr-12 py-3 bg-white/5 border ${
                    errors.confirmPassword ? 'border-red-500' : 'border-white/20'
                  } text-white placeholder-gray-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all duration-300`}
                  placeholder="请再次输入密码"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-white transition-colors"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.confirmPassword && (
                <div className="flex items-center gap-1 mt-1 text-red-400 text-sm">
                  <AlertCircle size={16} />
                  <span>{errors.confirmPassword}</span>
                </div>
              )}
            </div>
          )}

          {/* 提交按钮 */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 disabled:from-gray-500 disabled:to-gray-600 text-white font-semibold py-3 rounded-xl transition-all duration-300 shadow-lg shadow-cyan-500/25 hover:shadow-xl hover:shadow-cyan-500/40 transform hover:scale-105 disabled:transform-none disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>处理中...</span>
              </>
            ) : (
              <span>{mode === 'login' ? '登录' : '注册'}</span>
            )}
          </button>
        </form>

        {/* 底部链接 */}
        <div className="mt-6 text-center">
          {mode === 'login' ? (
            <div className="space-y-2">
              <p className="text-white/70 text-sm">
                还没有账户？
                <button
                  onClick={() => switchMode('register')}
                  className="text-cyan-400 hover:text-cyan-300 ml-1 font-medium"
                >
                  立即注册
                </button>
              </p>
              <button
                onClick={() => switchMode('forgot')}
                className="text-white/70 hover:text-white text-sm"
              >
                忘记密码？
              </button>
            </div>
          ) : mode === 'register' ? (
            <p className="text-white/70 text-sm">
              已有账户？
              <button
                onClick={() => switchMode('login')}
                className="text-cyan-400 hover:text-cyan-300 ml-1 font-medium"
              >
                立即登录
              </button>
            </p>
          ) : (
            <button
              onClick={() => switchMode('login')}
              className="text-cyan-400 hover:text-cyan-300 text-sm font-medium"
            >
              返回登录
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default AuthModal
